import React, { useState } from 'react';
import Table from 'cccisd-table';
import notification from 'cccisd-notification';
import Toggle from 'cccisd-toggle';
import Modal from 'cccisd-modal';
import _get from 'lodash/get';
import axios from 'cccisd-axios';
import HiddenBlock from 'cccisd-hidden-block';

import PencilIcon from 'cccisd-icons/pencil5';
import IconFlag from 'cccisd-icons/flag7';

import { getFormattedDateForDB } from '../helpers';

import style from './style.css';

const Boilerplate = window.cccisd && window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const getLogEntry = other => {
    return {
        updatedAt: new Date().toISOString(),
        updatedBy: {
            pawnId: Fortress.user.acting.id,
            userId: Fortress.user.acting.user.id,
            firstName: Fortress.user.acting.user.first_name,
            lastName: Fortress.user.acting.user.last_name,
            username: Fortress.user.acting.user.username,
            email: Fortress.user.acting.user.email,
        },
        ...other,
    };
};

const AlertsTable = props => {
    const { orderBy, youth, setAlertsEditedSignal } = props;

    const [resolvedToggleValue, setResolvedToggleValue] = useState({});
    const [noteInput, setNoteInput] = useState(null);
    const [lastNoteEdited, setLastNoteEdited] = useState(null);
    const [alerts, setAlerts] = useState(_get(youth, 'flags', []));

    // edit alert info so that it is easier to show on table
    const processedAlerts = alerts.map(alert => {
        const copy = { ...alert };

        // sort logs so latest changes show on top
        if (_get(copy, 'log', [])) {
            copy.log = [...copy.log].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        }

        // parse response to remove question variable names
        if (copy.response) {
            const match = copy.response.match(/'([^']*)'/);
            copy.response = match ? match[1] : copy.response;
        }

        // strip time so only date shows in table
        copy.unflaggedAt = copy.unflaggedAt ? copy.unflaggedAt.split(' ')[0] : '';

        return copy;
    });

    const columns = [
        {
            name: 'critical',
            label: 'Critical',
            sort: true,
            render: isCritical => {
                return (
                    <div className={`${style.critical_flag} ${isCritical ? style.critical_flag_active : ''}`}>
                        {isCritical ? <IconFlag /> : ''}
                    </div>
                );
            },
        },
        {
            name: 'surveyItem',
            label: 'Survey Item',
            filter: true,
            render: surveyItem => {
                return (
                    <div className={style.center_cell_200}>
                        <div>{surveyItem}</div>
                    </div>
                );
            },
        },
        {
            name: 'respondent',
            label: 'Respondent',
            sort: true,
            filter: true,
        },
        {
            name: 'wrapId',
            label: 'WrapID',
            sort: true,
            filter: true,
        },
        {
            name: 'date',
            label: 'Date Triggered',
            sort: true,
            filter: true,
        },
        {
            name: 'unflaggedAt',
            label: 'Date Resolved',
            sort: true,
            filter: true,
        },
        {
            name: 'response',
            label: 'Response',
            filter: true,
            render: response => {
                return (
                    <div className={style.center_cell_200}>
                        <div>{response}</div>
                    </div>
                );
            },
        },
        {
            name: 'notes',
            label: 'Note',
            filter: true,
            // When a note is added like 'test', and then later removed, 'test' still shows up in the alert as the current note.
            // latest entry in the log shows correctly that the latest note should be empty
            // possible bug in cccisd/assignment/src/Controllers/ProgressFlagController.php?
            render: notes => {
                return (
                    <div className={style.center_cell_200}>
                        <div>{notes}</div>
                    </div>
                );
            },
        },
        {
            name: 'unflaggedReason',
            label: 'Expired',
            filter: false,
            sort: true,
            render: unflaggedReason => {
                if (unflaggedReason === 'expired') {
                    return <div>Yes</div>;
                }
            },
        },
        {
            name: 'actions',
            label: 'Actions',
            render: (actions, row) => {
                return (
                    <div className={style.alerts_actions_cell} key={row.key}>
                        {actions.map((action, i) => {
                            let button = <></>;
                            if (action === 'resolve') {
                                button = (
                                    <div key={i}>
                                        <span className="form-label">Resolved:</span>

                                        <Toggle
                                            disabled={row.unflaggedReason === 'expired'}
                                            onChange={async val => {
                                                const newVal = !_get(
                                                    resolvedToggleValue,
                                                    row.flagProgressId,
                                                    row.status === 'unflagged'
                                                );
                                                setResolvedToggleValue({
                                                    ...resolvedToggleValue,
                                                    [row.flagProgressId]: newVal,
                                                });

                                                setAlerts(
                                                    alerts.map(alert => {
                                                        if (alert.flagProgressId !== row.flagProgressId) {
                                                            return alert;
                                                        }

                                                        const reason = newVal
                                                            ? 'manually_resolved'
                                                            : 'manually_unresolved';
                                                        const status = newVal ? 'unflagged' : 'flagged';
                                                        const log = [
                                                            ...(_get(alert, 'log', []) || []),
                                                            getLogEntry({
                                                                notes: alert.notes,
                                                                reason,
                                                                status,
                                                            }),
                                                        ];

                                                        return {
                                                            ...alert,
                                                            reason,
                                                            status,
                                                            unflaggedAt: getFormattedDateForDB(),
                                                            log,
                                                        };
                                                    })
                                                );

                                                // re-query youthlist query in ../Administer/index.js
                                                setAlertsEditedSignal(prev => !prev);

                                                if (newVal) {
                                                    const response = await axios.put(
                                                        Boilerplate.route('api.assignment.flag.progress.update', {
                                                            progressFlag: row.flagProgressId,
                                                        }),
                                                        {
                                                            reason: 'manually_resolved',
                                                            status: 'unflagged',
                                                            notes: row.notes,
                                                        }
                                                    );

                                                    if (response.status === 200 && response.data.status === 'success') {
                                                        notification({
                                                            message: 'Flag marked as resolved',
                                                            duration: 5000,
                                                        });
                                                    } else {
                                                        notification({
                                                            message: 'Error marking flag as resolved',
                                                            type: 'danger',
                                                        });
                                                    }
                                                } else {
                                                    const response = await axios.put(
                                                        Boilerplate.route('api.assignment.flag.progress.update', {
                                                            progressFlag: row.flagProgressId,
                                                        }),
                                                        {
                                                            reason: 'manually_unresolved',
                                                            status: 'flagged',
                                                            notes: row.notes,
                                                        }
                                                    );

                                                    if (response.status === 200 && response.data.status === 'success') {
                                                        notification({
                                                            message: 'Flag marked as unresolved',
                                                            duration: 5000,
                                                        });
                                                    } else {
                                                        notification({
                                                            message: 'Error marking flag as unresolved',
                                                            type: 'danger',
                                                        });
                                                    }
                                                }
                                            }}
                                            value={_get(
                                                resolvedToggleValue,
                                                row.flagProgressId,
                                                row.status === 'unflagged' &&
                                                    row.unflaggedReason === 'manually_resolved'
                                            )}
                                        />
                                    </div>
                                );
                            } else if (action === 'addNote') {
                                button = (
                                    <Modal
                                        key={i}
                                        trigger={
                                            <button
                                                type="button"
                                                className={`btn btn-xs btn-success ${style.action_button}`}
                                                onClick={e => {
                                                    if (lastNoteEdited !== row.flagProgressId) {
                                                        setNoteInput(null);
                                                    }
                                                }}
                                            >
                                                Edit Note <PencilIcon />
                                            </button>
                                        }
                                        title="Edit Note"
                                        size="medium"
                                        contentStyle={{ marginTop: '50px' }}
                                        onTop
                                        render={({ closeModal }) => {
                                            return (
                                                <div className={style.modal_note_container}>
                                                    <div>
                                                        <textarea
                                                            className={style.note_textarea}
                                                            maxLength="1000"
                                                            name="body"
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setNoteInput(val);
                                                                setLastNoteEdited(row.flagProgressId);
                                                            }}
                                                            value={noteInput === null ? row.notes : noteInput}
                                                        />
                                                    </div>
                                                    <div className={style.stop_plan_confirm_container}>
                                                        <button
                                                            type="button"
                                                            onClick={() => closeModal()}
                                                            className="btn btn-warning"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={async () => {
                                                                const response = await axios.put(
                                                                    Boilerplate.route(
                                                                        'api.assignment.flag.progress.update',
                                                                        {
                                                                            progressFlag: row.flagProgressId,
                                                                        }
                                                                    ),
                                                                    {
                                                                        progressFlag: row.flagProgressId,
                                                                        reason: 'Manual note entry',
                                                                        status: 'flagged',
                                                                        notes: noteInput,
                                                                    }
                                                                );

                                                                if (
                                                                    response.status === 200 &&
                                                                    response.data.status === 'success'
                                                                ) {
                                                                    notification({
                                                                        message: 'Note Added',
                                                                        duration: 5000,
                                                                    });

                                                                    closeModal();

                                                                    // re-query youthlist query in ../Administer/index.js
                                                                    setAlertsEditedSignal(prev => !prev);

                                                                    setAlerts(
                                                                        alerts.map(alert => {
                                                                            if (
                                                                                alert.flagProgressId ===
                                                                                row.flagProgressId
                                                                            ) {
                                                                                alert.notes = noteInput;

                                                                                if (!_get(alert, 'log', false)) {
                                                                                    alert.log = [];
                                                                                }
                                                                                alert.log.push(
                                                                                    getLogEntry({
                                                                                        notes: noteInput,
                                                                                        reason: alert.reason,
                                                                                        status: alert.status,
                                                                                    })
                                                                                );
                                                                            }

                                                                            return alert;
                                                                        })
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            Confirm
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                );
                            } else if (action === 'viewLogs') {
                                button = (
                                    <Modal
                                        key={i}
                                        trigger={
                                            <button
                                                type="button"
                                                className={`btn btn-xs btn-secondary ${style.action_button}`}
                                            >
                                                View Logs
                                            </button>
                                        }
                                        title="Logs"
                                        size="large"
                                        contentStyle={{ marginTop: '50px' }}
                                        onTop
                                        render={({ closeModal }) => {
                                            return (
                                                <div className={style.modal_logs_container}>
                                                    <div>
                                                        <Table
                                                            rowKey="updatedAt"
                                                            perPage={10}
                                                            orderBy={orderBy}
                                                            isAscentOrder={false}
                                                            columns={[
                                                                {
                                                                    name: 'status',
                                                                    label: 'Status',
                                                                    // sort: true,
                                                                    render: status => {
                                                                        return (
                                                                            <div
                                                                                className={`${style.status_pill} ${
                                                                                    status === 'flagged'
                                                                                        ? style.status_flagged
                                                                                        : style.status_resolved
                                                                                }`}
                                                                            >
                                                                                {status === 'flagged'
                                                                                    ? 'Unresolved'
                                                                                    : 'Resolved'}
                                                                            </div>
                                                                        );
                                                                    },
                                                                },
                                                                {
                                                                    name: 'notes',
                                                                    label: 'Note',
                                                                    filter: true,
                                                                },
                                                                {
                                                                    name: 'updatedAt',
                                                                    label: 'Updated At',
                                                                    filter: true,
                                                                    render: updatedAt => {
                                                                        return new Date(updatedAt).toLocaleString();
                                                                    },
                                                                },
                                                                {
                                                                    name: 'updatedBy',
                                                                    label: 'Updated By',
                                                                    filter: true,
                                                                    render: updatedBy => {
                                                                        return (
                                                                            <div>
                                                                                {`${updatedBy.firstName} ${updatedBy.lastName}`}
                                                                            </div>
                                                                        );
                                                                    },
                                                                },
                                                            ]}
                                                            data={row.log ? row.log : []}
                                                            showRowActions={false}
                                                            hideShowingRowsSummary
                                                        />
                                                    </div>
                                                    <div className={style.stop_plan_confirm_container}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={() => {
                                                                closeModal();
                                                            }}
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                );
                            }
                            return button;
                        })}
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <Table
                rowKey="flagProgressId"
                orderBy={orderBy}
                isAscentOrder={false}
                columns={columns.filter(column => column.name !== 'unflaggedReason' && column.name !== 'unflaggedAt')}
                data={processedAlerts.filter(alert => !(alert.status === 'unflagged'))}
                showRowActions={false}
                hideShowingRowsSummary
                showPerPageOptions={false}
            />
            <br />
            <HiddenBlock title="Resolved / Expired">
                <Table
                    rowKey="flagProgressId"
                    orderBy={orderBy}
                    isAscentOrder={false}
                    columns={columns}
                    data={processedAlerts.filter(alert => alert.status === 'unflagged')}
                    showRowActions={false}
                    hideShowingRowsSummary
                    showPerPageOptions={false}
                />
            </HiddenBlock>
        </div>
    );
};

export default AlertsTable;
